import DeleteOrganization from '@Application/Organizations/Organizations/Commands/DeleteOrganization';
import deleteOrganization from '@Application/Organizations/Organizations/deleteOrganization';
import RemoveAccountLogo from '@Application/Branding/AccountLogo/Commands/RemoveAccountLogo';
import removeAccountLogo from '@Application/Branding/AccountLogo/removeAccountLogo';
import RemoveOrganizationLogo from '@Application/Branding/OrganizationLogo/Commands/RemoveOrganizationLogo';
import removeOrganizationLogo from '@Application/Branding/OrganizationLogo/removeOrganizationLogo';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import logoToRemoveSignal, {
	cancelLogoRemoval,
} from '@UI/Pages/Administration/Organizations/Signals/logoToRemoveSignal';
import organizationToDeleteSignal, {
	setOrganizationToDelete,
} from '@UI/Pages/Administration/Organizations/Signals/organizationToDeleteSignal';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import { ConfirmDeleteDialog, ConfirmDialog } from '@verclam/ui-kit';

/**
 * The deletion confirmation dialog, mounted unconditionally alongside the organizations list.
 * Self-contained — reads the organizations list already fetched by `OrganizationsContent` through a
 * plain `useSelector` (never a second `useStoreQuery`, which would fire a second network call), and
 * the pending-deletion signal, rather than through props: the page has nothing of its own to pass it.
 */
const OrganizationsConfirmationDialogs: React.FC = () => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;
	const organizations = useSelector((state) => state.organizationsStore.organizations);
	const organizationToDeleteId = useSignalValue(organizationToDeleteSignal);

	const organizationBeingDeleted = React.useMemo(
		() =>
			organizationToDeleteId !== null
				? (organizations?.findById(organizationToDeleteId) ?? null)
				: null,
		[organizations, organizationToDeleteId],
	);

	const handleDeleteDialogHide = React.useCallback(() => {
		setOrganizationToDelete(null);
	}, []);

	const handleDeleteAccept = React.useCallback(() => {
		if (accountId === null || organizationToDeleteId === null) {
			return;
		}

		dispatch(deleteOrganization(new DeleteOrganization(accountId, organizationToDeleteId))).catch(
			ignoreHandledError,
		);
		// Close immediately: `acceptCallback` never triggers `onHide`, and waiting for the request to
		// settle would leave the dialog open long enough for a second click.
		setOrganizationToDelete(null);
	}, [accountId, organizationToDeleteId, dispatch]);

	const logoToRemove = useSignalValue(logoToRemoveSignal);

	const handleLogoRemovalAccept = React.useCallback(() => {
		if (accountId === null || logoToRemove === null) {
			return;
		}

		const removal =
			logoToRemove.owner === 'account'
				? dispatch(removeAccountLogo(new RemoveAccountLogo(accountId)))
				: dispatch(removeOrganizationLogo(new RemoveOrganizationLogo(logoToRemove.organizationId)));

		removal.catch(ignoreHandledError);
		cancelLogoRemoval();
	}, [accountId, logoToRemove, dispatch]);

	const handleLogoRemovalHide = React.useCallback(() => {
		cancelLogoRemoval();
	}, []);

	return (
		<React.Fragment>
			<ConfirmDeleteDialog
				visible={organizationToDeleteId !== null}
				onHide={handleDeleteDialogHide}
				header="Supprimer la structure"
				message={
					<React.Fragment>
						Cette action est irréversible. Confirmer la suppression de la structure{' '}
						<strong>{organizationBeingDeleted?.name ?? ''}</strong> ?
					</React.Fragment>
				}
				confirmText={organizationBeingDeleted?.name ?? ''}
				acceptCallback={handleDeleteAccept}
			/>
			<ConfirmDialog
				blockScroll
				closable
				draggable={false}
				visible={logoToRemove !== null}
				onHide={handleLogoRemovalHide}
				header="Retirer le logo ?"
				message={
					<p>
						{logoToRemove?.owner === 'account'
							? "Toutes les structures sans logo propre afficheront le logo de l'application."
							: "La structure affichera le logo du compte, à défaut celui de l'application."}
					</p>
				}
				accept={handleLogoRemovalAccept}
				reject={handleLogoRemovalHide}
				acceptSeverity="danger"
			/>
		</React.Fragment>
	);
};

export default OrganizationsConfirmationDialogs;
